import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Box, Grid, Switch,
  FormControlLabel, Avatar, Chip, Divider, useTheme, useMediaQuery, Card, CardContent
} from '@mui/material';
import {
  Edit, Delete, Add, Article, Campaign, PushPin, NotificationsActive,
  PhotoCamera, Close
} from '@mui/icons-material';
import podcastService from '../../utils/podcastService';
import api from '../../utils/api';
import { snackbar, successMessage } from '../../utils/showMessage';
import moment from 'moment-jalaali';
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg'];

// Helper function for date display
moment.loadPersian({ usePersianDigits: false });

const formatJalaliDate = (gDate) => {
  if (!gDate) return '';
  const parsed = moment(gDate);
  if (!parsed.isValid()) return '';
  return parsed.format('jYYYY/jMM/jDD');
};

const parseJalaliToGregorian = (value) => {
  if (!value) return '';
  const normalized = value.replace(/[^0-9/-]/g, '').replace(/-/g, '/');
  const candidates = ['jYYYY/jMM/jDD', 'jYYYY/jM/jD', 'YYYY/MM/DD', 'YYYY-MM-DD'];
  for (const format of candidates) {
    const parsed = moment(normalized, format, true);
    if (parsed.isValid()) {
      return parsed.format('YYYY-MM-DD');
    }
  }
  return '';
};

const textFieldStyle = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.1)' }
  }
};

const PodcastManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [podcast, setpodcast] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',

    image: '',
    podcast: '',
    isActive: true,
    sendNotification: false,
    order: 0,
    publishDate: new Date().toISOString().split('T')[0],
    publishDateJalali: formatJalaliDate(new Date().toISOString()),
    tags: []
  });

  const loadpodcast = useCallback(async () => {
    try {
      setLoading(true);
      const data = await podcastService.getAll(null, true);
      setpodcast(data || []);
    } catch (err) {
      console.error('Error fetching podcast:', err);
      snackbar('خطا در بارگذاری پادکست');
      setpodcast([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadpodcast();
  }, [loadpodcast]);

  const handleOpen = (item = null) => {
    if (item) {
      setCurrent(item);
      setFormData({
        title: item.title || '',
        content: item.content || '',
        summary: item.summary || '',
        type: item.type || 'podcast',
        image: item.image || '',
        gallery: item.gallery || [],
        isActive: item.isActive !== undefined ? item.isActive : true,
        isPinned: item.isPinned || false,
        sendNotification: false,
        order: item.order || 0,
        publishDate: item.publishDate || '',
        publishDateJalali: formatJalaliDate(item.publishDate || ''),
        tags: item.tags || []
      });
    } else {
      setCurrent(null);
      setFormData({
        title: '',
        content: '',
        summary: '',
        type: 'podcast',
        image: '',
        gallery: [],
        isActive: true,
        isPinned: false,
        sendNotification: false,
        order: 0,
        publishDate: '',
        publishDateJalali: '',
        tags: []
      });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handlePublishDateInput = (value) => {
    const normalized = value.replace(/[^0-9/-]/g, '').replace(/-/g, '/');
    const gregorian = parseJalaliToGregorian(normalized);
    setFormData({
      ...formData,
      publishDateJalali: normalized,
      publishDate: gregorian
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'publishDate') {
      handlePublishDateInput(value);
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fd = new FormData();
    fd.append('file', file);

    try {
      const response = await api.post('/welfare/upload-image', fd);
      const filename = response.data.filename;
      setFormData({ ...formData, image: filename });
      successMessage('فایل با موفقیت آپلود شد');
    } catch (err) {
      console.error(err);
      snackbar('خطا در آپلود فایل');
    }
  };
  const handlePodcastUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fd = new FormData();
    fd.append('file', file);

    try {
      const response = await api.post('/welfare/upload-image', fd);
      const filename = response.data.filename;
      setFormData({ ...formData, podcast: filename });
      successMessage('فایل با موفقیت آپلود شد');
    } catch (err) {
      console.error(err);
      snackbar('خطا در آپلود فایل');
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setLoading(true);
    const uploadedImages = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/welfare/upload-image', formData);
        uploadedImages.push(response.data.filename);
      }
      setFormData({ ...formData, gallery: [...(formData.gallery || []), ...uploadedImages] });
      successMessage('تصاویر به گالری اضافه شدند');
    } catch (error) {
      console.error('Error uploading gallery images:', error);
      snackbar('خطا در آپلود تصاویر گالری');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveGalleryImage = (index) => {
    const newGallery = [...(formData.gallery || [])];
    newGallery.splice(index, 1);
    setFormData({ ...formData, gallery: newGallery });
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        tags: typeof formData.tags === 'string' ? formData.tags.split(',').map(t => t.trim()) : formData.tags
      };

      if (current) {
        const { sendNotification, ...updatePayload } = payload;
        await podcastService.update(current._id, updatePayload);
        successMessage('پادکست با موفقیت ویرایش شد');
      } else {
        await podcastService.create(payload);
        successMessage('پادکست با موفقیت ایجاد شد');
      }
      handleClose();
      loadpodcast();
    } catch (err) {
      console.error(err);
      snackbar(err?.response?.data?.message || 'خطا در ذخیره');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این پادکست اطمینان دارید؟')) return;
    try {
      await podcastService.delete(id);
      successMessage('پادکست با موفقیت حذف شد');
      loadpodcast();
    } catch (err) {
      console.error(err);
      snackbar('خطا در حذف');
    }
  };

  const handleResendNotification = async (id) => {
    if (!window.confirm('ارسال مجدد نوتیفیکیشن برای این پادکست انجام شود؟')) return;
    try {
      await api.post(`/podcast/${id}/send-notification`);
      successMessage('نوتیفیکیشن ارسال شد');
    } catch (err) {
      console.error(err);
      snackbar(err?.response?.data?.message || 'خطا در ارسال نوتیفیکیشن');
    }
  };

  const getImageUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('http')) return img;
    return `${process.env.REACT_APP_FILE_BASE_URL}/uploads/documents/${img}`;
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 3, mt: isMobile ? 3 : 5 }}>
      <Box sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: 2,
        mb: 3,
        mt: isMobile ? 6 : 5
      }}>
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">مدیریت پادکست و اطلاعیه‌ها</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpen()}
          fullWidth={isMobile}
          sx={{ borderRadius: '12px', py: isMobile ? 1.5 : 1 }}
        >
          پادکست جدید
        </Button>
      </Box>

      {isMobile ? (
        <Grid container spacing={2}>
          {podcast.map((item) => (
            <Grid item xs={12} key={item._id}>
              <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #eee', bgcolor: '#fafafa' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                    <Avatar
                      src={getImageUrl(item.image)}
                      variant="rounded"
                      sx={{ width: 80, height: 80, borderRadius: 3 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ maxWidth: '150px' }}>
                          {item.title}
                        </Typography>
                        <Chip
                          label={item.isActive ? 'فعال' : 'غیرفعال'}
                          size="small"
                          color={item.isActive ? 'success' : 'default'}
                          sx={{ borderRadius: '8px', height: 20, fontSize: '10px' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, my: 1 }}>
                        {item.type === 'announcement' ? (
                          <Chip icon={<Campaign sx={{ fontSize: '14px !important' }} />} label="اطلاعیه" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '10px' }} />
                        ) : (
                          <Chip icon={<Article sx={{ fontSize: '14px !important' }} />} label="پادکست" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '10px' }} />
                        )}
                        {item.isPinned && <PushPin sx={{ fontSize: 16, color: 'orange' }} />}
                      </Box>

                      <Typography variant="caption" color="textSecondary" display="block">
                        👁️ {item.viewCount || 0} بازدید | 📅 {formatJalaliDate(item.publishDate)}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 1.5 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <IconButton size="small" onClick={() => handleOpen(item)} sx={{ bgcolor: '#fff', border: '1px solid #eee' }}>
                      <Edit fontSize="small" color="primary" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleResendNotification(item._id)} sx={{ bgcolor: '#fff', border: '1px solid #eee' }}>
                      <NotificationsActive fontSize="small" color="info" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(item._id)} sx={{ bgcolor: '#fff', border: '1px solid #eee' }}>
                      <Delete fontSize="small" color="error" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {podcast.length === 0 && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 4, borderStyle: 'dashed' }}>
                <Typography variant="body2" color="textSecondary">پادکستی یافت نشد</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, border: '1px solid #eee' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell align="right">تصویر</TableCell>
                <TableCell align="right">عنوان</TableCell>
                <TableCell align="right">وضعیت</TableCell>
                {!isTablet && <TableCell align="right">بازدید</TableCell>}
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {podcast.map((item) => (
                <TableRow key={item._id} hover>
                  <TableCell align="right">
                    <Avatar src={getImageUrl(item.image)} variant="rounded" sx={{ width: 50, height: 50, borderRadius: 2 }} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>{item.title}</TableCell>

                  <TableCell align="right">
                    <Chip label={item.isActive ? 'فعال' : 'غیرفعال'} size="small" color={item.isActive ? 'success' : 'default'} />
                  </TableCell>

                  {!isTablet && <TableCell align="right">{item.viewCount || 0}</TableCell>}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleOpen(item)} title="ویرایش">
                        <Edit fontSize="small" color="primary" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleResendNotification(item._id)} title="ارسال نوتیفیکیشن">
                        <NotificationsActive fontSize="small" color="info" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(item._id)} title="حذف">
                        <Delete fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 4 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {current ? 'ویرایش پادکست' : 'ایجاد پادکست جدید'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="عنوان"
                name="title"
                value={formData.title}
                onChange={handleChange}
                sx={textFieldStyle}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="ترتیب نمایش"
                name="order"
                value={formData.order}
                onChange={handleChange}
                sx={textFieldStyle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="publishDate"
                label="تاریخ انتشار"
                value={formData.publishDateJalali}
                onChange={handleChange}
                placeholder="۱۴۰۳/۱۱/۰۳"
                sx={textFieldStyle}
              />
            </Grid>

            {/* <Grid item xs={12}>
              <AIRewriteTextField
                value={formData.content}
                onChange={(value) => setFormData({ ...formData, content: value })}
                label="توضیحات"
                rows={6}
                textFieldProps={{ sx: textFieldStyle }}
              />
            </Grid> */}


            {/* Featured Image Uplod */}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ borderRadius: '12px', borderStyle: 'dashed', py: 1.5 }}
              >
                <PhotoCamera sx={{ mr: 1 }} />
                آپلود تصویر شاخص پادکست
                <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
              </Button>
              {formData.image && (
                <Box mt={2} position="relative">
                  <img src={getImageUrl(formData.image)} alt="Preview" style={{ width: '100%', maxHeight: '180px', objectFit: 'cover', borderRadius: '12px', border: '1px solid #eee' }} />
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: '#fff' } }}
                    onClick={() => setFormData({ ...formData, image: '' })}
                  >
                    <Close fontSize="small" color="error" />
                  </IconButton>
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ borderRadius: '12px', borderStyle: 'dashed', py: 1.5 }}
              >
                <PhotoCamera sx={{ mr: 1 }} />
                آپلود پادکست
                <input type="file" hidden accept="audio/*,video/*" onChange={handlePodcastUpload} />
              </Button>
              {formData.podcast && (
                <Box mt={2} position="relative">
                  {formData.podcast && VIDEO_EXTENSIONS.some(ext => formData.podcast.toLowerCase().endsWith(ext)) ?
                    <video
                      controls
                      className="w-100 rounded shadow-sm" 
                    >
                      <source
                        src={getImageUrl(formData.podcast)}
                        type="video/mp4"
                      />
                    </video>
                    :
                    <audio className="wp-audio-shortcode"
                      preload="auto"
                      style={{ width: "100%" }}
                      controls="controls">
                      <source type="audio/mpeg" src={getImageUrl(formData.podcast)} />
                    </audio>}
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: '#fff' } }}
                    onClick={() => setFormData({ ...formData, podcast: '' })}
                  >
                    <Close fontSize="small" color="error" />
                  </IconButton>
                </Box>
              )}
            </Grid>



            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
                <FormControlLabel
                  control={<Switch checked={formData.isActive} onChange={handleSwitchChange} name="isActive" />}
                  label="فعال"
                />

                <FormControlLabel
                  control={<Switch checked={formData.sendNotification} onChange={handleSwitchChange} name="sendNotification" disabled={!!current} />}
                  label="ارسال نوتیفیکیشن"
                />
              </Box>
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} sx={{ borderRadius: '8px' }}>انصراف</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ borderRadius: '8px', px: 4 }}>
            ذخیره تغییرات
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};


export default PodcastManagement;
